import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from "@mui/material";
import { FormatDateOptions, useIntl } from "react-intl";

import EastIcon from "@mui/icons-material/East";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { useDevice } from "../utils/DeviceProvider";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { useApi } from "../utils/ApiProvider";
import { FirmwareUpdateHistoryItem, FirmwareUpdateHistoryLogResponse, FirmwareUpdateHistoryResponse } from "../utils/types";
import { useMutation } from "react-query";
import { CellProps, Column } from "react-table";
import { EnhancedTable } from "@mb-pro-ui/components/table";
import { useUserInfo } from "../utils/user-info";
import { useUpdateStatusLabel } from "./FirmwareUpdateDeviceItem";

const dateFormatOptions: FormatDateOptions = {
  dateStyle: "short",
  timeStyle: "medium",
};

export const FirmwareUpdateSubpage = () => {
  const [selectedHistory, setSelectedHistory] = useState<number | null>(null);
  const handleClose = () => setSelectedHistory(null);

  useEffect(() => {
    console.log("FirmwareUpdateSubpage");
  }, []);

  const {
    device: { unitid, unitDetails },
  } = useDevice();
  const history = useHistory();
  const { formatMessage, formatDate } = useIntl();
  const [userInfo] = useUserInfo();
  const { updateStatusLabel } = useUpdateStatusLabel();

  // TODO: többi előfordulható hiba
  const { errorHandler: onError } = useErrorHandler([]);
  const api = useApi();

  const getHistoryQuery = useCallback(() => api<FirmwareUpdateHistoryResponse>(`/units/${unitid}/firmware/history`), [api, unitid]);
  const { mutate: getHistory, data: updateHistory, status: queryStatus } = useMutation({ mutationFn: getHistoryQuery, onError });

  const getLogQuery = useCallback(
    (historyId: number) => api<FirmwareUpdateHistoryLogResponse>(`/units/${unitid}/firmware/history/${historyId}/log`),
    [api, unitid]
  );
  const { mutate: getHistoryLog, data } = useMutation({ mutationFn: getLogQuery, onError });
  const updateLog = useMemo(() => {
    if (!data) {
      return null;
    }
    if (data.log.startsWith("\n")) {
      return data.log.replace("\n", "");
    }
    return data.log;
  }, [data]);

  const goToFirmwareUpdate = useCallback(() => {
    if (!unitDetails) {
      return;
    }
    history.push(`/firmware-update?${unitDetails ? `hwtype=${unitDetails.hwtype}&hwver=${unitDetails.hwver}&` : ""}unitid=${unitid}`);
  }, [history, unitDetails, unitid]);

  const goToFirmware = useCallback(
    (firmwareId: number) => {
      if (!userInfo?.rights.firmware_upload) {
        return;
      }
      window.open(`/firmwares/${firmwareId}`, "_blank", "noopener,noreferrer");
    },
    [userInfo]
  );

  const columns = useMemo(
    (): Column<FirmwareUpdateHistoryItem>[] => [
      {
        id: "operator",
        Header: formatMessage({
          defaultMessage: "Operator",
        }),
        accessor: (item) => item.upgrader_fullname ?? item.upgrader_name,
        width: 70,
      },
      {
        id: "start-ts",
        Header: formatMessage({
          defaultMessage: "Started at",
        }),
        accessor: (item) => formatDate(item.upgrade_ts, dateFormatOptions),
        width: 100,
      },
      {
        id: "firmware",
        Header: formatMessage({
          defaultMessage: "Firmware",
        }),
        accessor: (item) => [item.firmware.version ?? item.firmware.swver, item.firmware.description].join(" "),
        Cell: ({ row }: CellProps<FirmwareUpdateHistoryItem>) => (
          <Box sx={{ width: 1 / 1, display: "flex", justifyContent: "space-between", alignItems: "center", overflow: "hidden" }}>
            <span style={{ flex: "1", overflow: "hidden", textOverflow: "ellipsis" }}>
              {[row.original.firmware.version ?? row.original.firmware.swver, row.original.firmware.description].join(" ")}
            </span>
            <IconButton size="small" onClick={() => goToFirmware(row.original.firmware.id)}>
              <OpenInNewIcon />
            </IconButton>
          </Box>
        ),
        width: 100,
      },
      {
        id: "status",
        Header: formatMessage({
          defaultMessage: "Status",
        }),
        accessor: (item) => updateStatusLabel(item.upgrade_status),
        width: 50,
      },
      {
        id: "log",
        Header: formatMessage({
          defaultMessage: "Log",
        }),
        Cell: ({ row }: CellProps<FirmwareUpdateHistoryItem>) => (
          <IconButton size="small" onClick={() => setSelectedHistory(row.original.id)}>
            <ArticleOutlinedIcon />
          </IconButton>
        ),
        width: 50,
      },
      {
        id: "tries",
        Header: formatMessage({
          defaultMessage: "Tries left",
        }),
        accessor: (item) => item.max_failures,
        width: 100,
      },
    ],
    [formatDate, formatMessage, goToFirmware, updateStatusLabel]
  );

  useEffect(() => {
    getHistory();
  }, [getHistory]);

  useEffect(() => {
    if (!selectedHistory) {
      return;
    }
    getHistoryLog(selectedHistory);
  }, [getHistoryLog, selectedHistory]);

  return (
    <>
      <Button sx={{ alignSelf: "flex-start", mb: 1 }} endIcon={<EastIcon />} onClick={goToFirmwareUpdate}>
        {formatMessage({ defaultMessage: "Go to firmware upgrade" })}
      </Button>
      <EnhancedTable
        columns={columns}
        data={updateHistory ?? []}
        showResultCount
        queryStatus={queryStatus}
        overscanCount={5}
        disableGlobalFilter
        postfix={
          <>
            <IconButton
              size="small"
              sx={{
                color: "primary.contrastText",
              }}
              onClick={() => getHistory()}
            >
              <RefreshIcon />
            </IconButton>
          </>
        }
      />
      <Dialog open={selectedHistory !== null} onClose={handleClose} fullScreen sx={{ m: { xs: 1, sm: 2, md: 3 } }}>
        <DialogTitle>{formatMessage({ defaultMessage: "Firmware update log" })}</DialogTitle>
        <DialogContent dividers>
          {updateLog ? (
            <DialogContentText sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}>{updateLog}</DialogContentText>
          ) : (
            <Box sx={{ height: 1 / 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{formatMessage({ defaultMessage: "Close" })}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
